import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { navigate } from 'gatsby';
import clsx from 'clsx';

import ToggleButton from '@material-ui/lab/ToggleButton';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Grid,
  Typography,
  Select,
  InputBase,
  IconButton,
  Button,
  Collapse,
  FormControl,
  MenuItem,
  Divider,
  Link,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import { MdFilterList as FilterListIcon, MdClose as CloseIcon } from 'react-icons/md';
import { RiSearch2Line as SearchIcon } from 'react-icons/ri';

import {
  PublicStatuses,
  StaffStatuses,
  DateRanges,
  ROWS_PER_PAGE,
  SORT_ORDER,
  PublicReviewColumns,
  StaffReviewColumns,
  ActionType,
  SystemAdminEmail,
} from '../../constants';
import Table from './../table';
import { generateQuery } from '../../adapters';
import { isStaffUser, getUser, isLoggedIn } from '../../contexts/auth';
import { OrsRoutes } from '../../routes';
import { MailTo, ReviewStatusEnum } from '@eisc-frontend/shared-ui';

const user = getUser();
const isStaff = isStaffUser();

export const ReviewActions = [
  {
    label: ActionType.VIEW,
    onClick: (id) => {
      window.open(`/submission/${id}`);
    },
    isHidden: () => false,
  },
  {
    label: ActionType.EDIT,
    onClick: (id) => {
      window.open(OrsRoutes.EditReview.replace(':reviewId', id), '_self');
    },
    isHidden: (item) => {
      const authorizedDeveloper = user?.organizations?.some((o) => o.name === item.developer);
      return !((isStaff || authorizedDeveloper) && ReviewStatusEnum.Closed !== item.status);
    },
  },
];

const useStyles = makeStyles((theme) => ({
  tab: {
    border: `1px solid ${theme.palette.secondary.main}`,
    backgroundColor: theme.palette.common.white,
    padding: '8px',
    borderRadius: '4px',
    fontWeight: 600,
    fontSize: '14px',
    marginRight: '5px',
    marginBottom: '10px',
    color: theme.palette.text.primary,
    '&.Mui-selected': {
      backgroundColor: theme.palette.secondary.main,
      color: '#fff',
    },
    '&.Mui-selected:hover': {
      backgroundColor: `${theme.palette.secondary.main}aa`,
    },
  },
  searchBase: {
    border: '1px solid #ABCDEF40',
  },
  search: {
    flex: 1,
    padding: 7,
    fontSize: '13px',
    border: 'none',
    '& input': {
      backgroundColor: 'transparent',
      textOverflow: 'ellipsis',
    },
  },
  iconButton: {
    padding: 7,
    color: '#CCCED0',
  },
}));

const ReviewTable = ({
  isFetchingReviews,
  organizations,
  boards,
  reviews,
  totalReviewsCount,
  boardAbbr,
  boardName,
  boardImages,
  onUpdate,
  onBoardClicked,
}) => {
  const classes = useStyles();

  // filters
  const [isDropdownFilterOpen, setDropdownFilterOpen] = useState(false);
  const [searchFilter, setSearch] = useState('');
  const [statusFilter, setStatus] = useState('All');
  const [statusSearchFilter, setStatusSearch] = useState('All');
  const [dateFilter, setDate] = useState('All');
  const [developerFilter, setDeveloper] = useState('All');
  // Filter for projects located on the Yukon North Slope (PSQ Q8 = Yes)
  const [yukonNorthSlopeFilter, setYukonNorthSlopeFilter] = useState(false);

  const tableColumns = isStaff ? StaffReviewColumns : PublicReviewColumns;

  const filterReviews = (offset = 0, orderBy = 'createdAt', order = 0, abbr = null) => {
    console.log('searchFilter: ', searchFilter);
    onUpdate(
      generateQuery(
        searchFilter,
        statusSearchFilter,
        dateFilter,
        developerFilter,
        offset,
        orderBy,
        SORT_ORDER[order],
        abbr || boardAbbr || '',
        yukonNorthSlopeFilter,
      ),
    );
  };

  const handleUpdate = (abbr = null) => {
    (() => {
      if (reviews) {
        filterReviews(0, 'createdAt', 0, abbr);
      }
    })();
  };

  useEffect(() => {
    handleUpdate();
  }, [statusSearchFilter, developerFilter, dateFilter, yukonNorthSlopeFilter]);

  const currentBoardImage = boardImages?.find((el) => el.name === boardName);

  const userHasOrganization = () => {
    return user?.organizations?.filter((el) => el.name !== 'Member of the Public').length > 0;
  };

  return (
    <div className="p-4">
      {/** Title section */}
      <div className="my-4">
        <div className="flex justify-between items-center">
          <div className="my-2">
            <div className="flex items-center justify-start">
              {currentBoardImage?.imageMime && currentBoardImage?.imageBase64 && (
                <img
                  alt={boardName}
                  src={`data:${currentBoardImage.imageMime};base64,${currentBoardImage.imageBase64}`}
                  className="max-w-8"
                />
              )}
            </div>
            <Typography variant="h1">All Project Submissions</Typography>
          </div>
          {isLoggedIn() && userHasOrganization() && (
            <Button
              className="px-16 py-4"
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate(OrsRoutes.CreateReview)}
            >
              Create New Submission
            </Button>
          )}
        </div>
        <Typography variant="body1" className="pb-2">
          Project Submissions can be viewed by Community, Phase, Developer, or Date.
        </Typography>
        <Typography variant="body1" className="pb-2">
          To create a new submission or make comments to an existing submission, please contact the{' '}
          {<MailTo text={SystemAdminEmail.name} email={SystemAdminEmail.email} />} to be assigned to
          an organization.
        </Typography>
        <Link
          href={`${process.env.GATSBY_SUBMISSION_WORKFLOW_URL}`}
          rel="noopener"
          target="_blank"
          className="underline font-semibold"
        >
          <Typography variant="subtitle1" className="text-blue-900">
            See the project submission workflow
          </Typography>
        </Link>
      </div>
      <Divider />

      <div className="py-8">
        {/** Toolbar */}
        <Grid container>
          {/** Tabs */}
          <Grid item xs={12} sm={7}>
            {boards && boards.length > 0 && (
              <Box>
                {boards.map(({ abbr, label }) => (
                  <ToggleButton
                    data-test={`reviews-data-table-board-toggle-button-${abbr}`}
                    className={classes.tab}
                    key={abbr}
                    value={abbr}
                    size="small"
                    selected={(!boardAbbr && abbr === 'All') || abbr === boardAbbr}
                    onClick={() => {
                      if (onBoardClicked) onBoardClicked();
                      navigate(
                        abbr === 'All'
                          ? '/submissions'
                          : `/submissions/board/${encodeURIComponent(abbr.toLowerCase())}/`,
                      );
                      if (boardAbbr) {
                        handleUpdate(abbr);
                      }
                    }}
                  >
                    {label}
                  </ToggleButton>
                ))}
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sm={5}>
            <Grid container spacing={2}>
              {/** Search Bar */}
              <Grid item xs={12} sm={9}>
                <div
                  className={clsx(
                    classes.searchBase,
                    'max-h-9 flex min-w-24 text-base leading-6 rounded',
                  )}
                >
                  <InputBase
                    className={classes.search}
                    placeholder="Search title, file #, developer or contact name"
                    onChange={(event) => setSearch(event?.target?.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleUpdate()}
                  />
                  <IconButton onClick={() => handleUpdate()}>
                    <SearchIcon />
                  </IconButton>
                </div>
              </Grid>

              {/** Filter */}
              <Grid item xs={12} sm={3}>
                <Button
                  className="h-9"
                  size="medium"
                  variant="outlined"
                  onClick={() => setDropdownFilterOpen(!isDropdownFilterOpen)}
                  endIcon={isDropdownFilterOpen ? <CloseIcon /> : <FilterListIcon />}
                >
                  Filter
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Typography className="py-5" variant="subtitle1">
          Showing {totalReviewsCount} Project Submissions
        </Typography>
        <Divider />
        {/** Filters */}
        <Box my={2}>
          <Collapse in={isDropdownFilterOpen}>
            <Grid justify="space-evenly" container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined">
                  <p className="font-sm m-1.5 mt-0 p-0">{isStaff ? 'Status' : 'Phase'}</p>
                  <Select
                    value={statusFilter}
                    onChange={(event) => {
                      const status = event?.target?.value.toString();
                      setStatus(status);
                      setStatusSearch(status === 'All' ? status : `&status=${status}`);
                    }}
                    name="filterStatus"
                  >
                    {(isStaff ? StaffStatuses : PublicStatuses).map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined">
                  <p className="font-sm m-1.5 mt-0 p-0">Developer</p>
                  <Select
                    value={developerFilter}
                    name="selectDeveloper"
                    onChange={(event) => setDeveloper(event?.target?.value.toString())}
                  >
                    <MenuItem key={'All'} value={'All'}>
                      All
                    </MenuItem>
                    {organizations.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined">
                  <p className="font-sm m-1.5 mt-0 p-0">Date Range</p>
                  <Select
                    value={dateFilter}
                    name="selectDate"
                    onChange={(event) => setDate(event?.target?.value.toString())}
                  >
                    {DateRanges.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/** Yukon North Slope filter (PSQ Q8 = Yes) */}
              <Grid item xs={12}>
                <FormControlLabel
                  style={{ marginLeft: 0 }}
                  labelPlacement="end"
                  control={
                    <Checkbox
                      checked={yukonNorthSlopeFilter}
                      name="filterYukonNorthSlope"
                      color="primary"
                      onChange={(event) => setYukonNorthSlopeFilter(event.target.checked)}
                    />
                  }
                  label="Located on Yukon North Slope"
                />
              </Grid>
            </Grid>
          </Collapse>
        </Box>

        {/** Table */}
        <Table
          isFetching={isFetchingReviews}
          columns={tableColumns}
          rows={reviews}
          rowsPerPage={ROWS_PER_PAGE}
          totalRowsCount={totalReviewsCount}
          onUpdate={(offset, orderBy, order) => {
            if (reviews) {
              filterReviews(offset, orderBy, order);
            }
          }}
          actions={ReviewActions}
          collapsible="related"
          pagination
          filtersChange={`${statusSearchFilter}${developerFilter}${dateFilter}${yukonNorthSlopeFilter}`}
        />
      </div>
    </div>
  );
};

ReviewTable.propTypes = {
  isFetching: PropTypes.bool,
  isFetchingReviews: PropTypes.bool,
  organizations: PropTypes.array.isRequired,
  boards: PropTypes.array.isRequired,
  reviews: PropTypes.array,
  totalReviewsCount: PropTypes.number.isRequired,
  boardAbbr: PropTypes.string,
  boardName: PropTypes.string,
  boardImages: PropTypes.array,
  onUpdate: PropTypes.any,
  onBoardClicked: PropTypes.any,
};

export default ReviewTable;
